import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { PageSpinner } from '@ohpen/enso-ui';
import { SignInPage } from '@ohpen/react-auth';
import { getCdnAssets, useConfig, type CdnAssets } from '@ohpen/react-utils';

export const Login = () => {
  const config = useConfig();
  const auth = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const providerIds = config?.AWS?.IDENTITY_PROVIDERS || [];
  const providers = providerIds.map(id => ({ id, name: id, type: 'azure' as const }));
  const enableCredentials = config?.AWS?.ENABLE_CREDENTIALS_LOGIN ?? false;
  const hasSSO = providers.length > 0;

  const [cdnAssets, setCdnAssets] = useState<CdnAssets | null>(null);

  useEffect(() => {
    getCdnAssets(config).then(assets => {
      if (assets) setCdnAssets(assets);
    });
  }, [config]);

  // For SSO the destination the user was after travels in the OIDC state parameter.
  const from = (location.state as { from?: string } | null)?.from || '/';

  // Both OIDC and Amplify credentials report their session asynchronously, so the form waits for
  // them to avoid flashing in front of somebody who is already signed in.
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (auth.isLoading) return;

    const checkAuth = async () => {
      if (auth.isAuthenticated) {
        navigate(from, { replace: true });
        return;
      }

      if (enableCredentials) {
        try {
          await getCurrentUser();
          navigate(from, { replace: true });
          return;
        } catch {
          // Not signed in with credentials either: the form is shown.
        }
      }

      setChecking(false);
    };

    checkAuth();
  }, [auth.isLoading, auth.isAuthenticated, enableCredentials, navigate, from]);

  if (checking) return <PageSpinner />;

  const handleProviderLogin = async (providerId: string) => {
    await auth.signinRedirect({
      extraQueryParams: { identity_provider: providerId },
      state: { from },
    });
  };

  const handleAfterSignIn = async () => {
    navigate(from, { replace: true });
  };

  const getDescription = () => {
    if (hasSSO && enableCredentials) return t('portal.login.descriptionSsoAndCredentials');
    if (enableCredentials) return t('portal.login.descriptionCredentialsOnly');
    return t('portal.login.descriptionSsoOnly');
  };

  return (
    <SignInPage
      logo={cdnAssets?.logoPath ?? ''}
      description={getDescription()}
      providers={providers}
      enableCredentials={enableCredentials}
      onProviderLogin={handleProviderLogin}
      onAfterSignIn={handleAfterSignIn}
      useAuthContext={false}
    />
  );
};
